import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, ChevronRight, Instagram, Youtube, Music2, Send } from 'lucide-react';
import axios from 'axios';
import TrackCard from '../components/TrackCard';
import { usePlayer } from '../contexts/PlayerContext';
import logger from '../utils/logger';
import HomepageHeroAd from '../components/ads/HomepageHeroAd';
import RecentPurchases from '../components/RecentPurchases';
import LocalLawFinderSpotlight from '../components/LocalLawFinderSpotlight';
import LiveViewersBadge from '../components/LiveViewersBadge';
import FamilyPulseCard from '../components/FamilyPulseCard';

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [data, setData] = useState(null);
  const [artist, setArtist] = useState(null);
  const [settings, setSettings] = useState({});
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [collections, setCollections] = useState([]);
  const { playTrack } = usePlayer();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [homeRes, artistRes, collectionsRes] = await Promise.all([
          axios.get(`${API}/api/homepage`),
          axios.get(`${API}/api/artist`),
          axios.get(`${API}/api/collections`)
        ]);
        setData(homeRes.data);
        setSettings(homeRes.data.settings || {});
        setArtist(artistRes.data);
        setCollections(collectionsRes.data);
      } catch (e) {
        logger.error('Failed to load homepage', e);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const formData = new FormData();
      formData.append('email', email);
      await axios.post(`${API}/api/newsletter/subscribe`, formData);
      setSubscribed(true);
      setEmail('');
    } catch (e) {
      logger.error('Subscribe failed', e);
    }
  };

  const handlePlayHero = () => {
    if (data?.new_releases?.[0]) {
      playTrack(data.new_releases[0], data.new_releases);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-[#0D0D0D] z-10" />
        <div
          className="absolute inset-0 bg-cover bg-center"
          data-testid="hero-bg"
          style={{
            backgroundImage: `url(${settings?.hero_image_url || artist?.images?.hero || 'https://customer-assets.emergentagent.com/job_promptlab-42/artifacts/6bny6hzt_1000002473.jpg'})`,
          }}
        />
        
        <div className="relative z-20 text-center px-4 max-w-4xl mx-auto animate-fade-in-up">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4 stagger-1">
            Young Black Man Inc.
          </p>
          <h1 className="font-['Bebas_Neue'] text-6xl md:text-8xl lg:text-9xl tracking-tight leading-none mb-6 stagger-2">
            IAMHETHATIS
          </h1>
          <p className="text-lg md:text-xl text-[#A3A3A3] font-light mb-2 stagger-3">
            (pronounced: I AM, He That Is)
          </p>
          <p className="text-xl md:text-2xl text-white/80 font-light max-w-2xl mx-auto mb-8 stagger-4">
            {artist?.tagline || 'You Become What You Think About'}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 stagger-5">
            <button
              onClick={handlePlayHero}
              className="px-8 py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider flex items-center gap-3 hover:bg-[#E5C048] transition-all animate-pulse-gold"
              data-testid="hero-play-button"
            >
              <Play size={20} />
              Play Latest Release
            </button>
            <Link
              to="/music"
              className="px-8 py-4 border border-[#D4AF37] text-[#D4AF37] font-medium uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
              data-testid="hero-explore-button"
            >
              Explore Music
            </Link>
          </div>

          {/* Live viewers — transparent, real-time visibility */}
          <div className="mt-10 flex justify-center stagger-5">
            <LiveViewersBadge variant="hero" />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-[#D4AF37] rounded-full" />
          </div>
        </div>
      </section>

      {/* New Music Section */}
      <LocalLawFinderSpotlight />
      <HomepageHeroAd />
      <RecentPurchases />

      {/* Family vibe pulse — fan-facing live activity snapshot */}
      <section className="px-6 md:px-12 -mt-8 md:-mt-4 mb-8" data-testid="family-pulse-section">
        <FamilyPulseCard />
      </section>

      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="new-music-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Fresh Drops</p>
              <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl">NEW MUSIC</h2>
            </div>
            <Link to="/music" className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
              <span className="text-sm uppercase tracking-wider">View All</span>
              <ChevronRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {data?.new_releases?.slice(0, 4).map((track, idx) => (
              <div key={track.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.1}s` }}>
                <TrackCard track={track} tracks={data.new_releases} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Tracks Section */}
      <section className="py-24 bg-[#1A1A1A]" data-testid="top-tracks-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Most Played</p>
              <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl">TOP TRACKS</h2>
            </div>
          </div>

          <div className="space-y-2">
            {data?.top_tracks?.slice(0, 5).map((track, idx) => (
              <div key={track.id} className="flex items-center gap-4 animate-fade-in-up" style={{ animationDelay: `${idx * 0.1}s` }}>
                <span className="text-3xl font-['Bebas_Neue'] text-[#D4AF37] w-8">{idx + 1}</span>
                <div className="flex-1">
                  <TrackCard track={track} tracks={data.top_tracks} compact />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shop Section - Links to Shopify */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="shop-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Exclusive Merch</p>
              <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl">SHOP NEW DROPS</h2>
            </div>
            <a
              href={artist?.shopify_url || 'https://youngblackmaninc.com'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
            >
              <span className="text-sm uppercase tracking-wider">Shop Now</span>
              <ChevronRight size={18} />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {data?.featured_products?.map((product, idx) => (
              <a
                key={product.id}
                href={artist?.shopify_url || 'https://youngblackmaninc.com'}
                target="_blank"
                rel="noopener noreferrer"
                className="group animate-fade-in-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="relative aspect-square bg-[#1A1A1A] overflow-hidden">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]" />
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider">
                      Shop Now
                    </span>
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="font-medium group-hover:text-[#D4AF37] transition-colors">{product.name}</h3>
                  <p className="text-[#D4AF37] font-['Bebas_Neue'] text-2xl mt-1">
                    ${product.price.toLocaleString()}
                  </p>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href={artist?.shopify_url || 'https://youngblackmaninc.com'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
              data-testid="shop-cta"
            >
              Visit Full Store
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-[#1A1A1A]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">About The Artist</p>
              <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl mb-6">IAMHETHATIS</h2>
              <p className="text-[#A3A3A3] leading-relaxed mb-6">
                {artist?.bio}
              </p>
              <p className="text-white font-medium mb-8">
                {artist?.roles?.join(' • ')}
              </p>

              {/* Social Links */}
              <div className="flex gap-4">
                {artist?.social?.spotify && (
                  <a href={artist.social.spotify} target="_blank" rel="noopener noreferrer" className="p-3 bg-[#262626] hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid="social-spotify">
                    <Music2 size={20} />
                  </a>
                )}
                {artist?.social?.youtube && (
                  <a href={artist.social.youtube} target="_blank" rel="noopener noreferrer" className="p-3 bg-[#262626] hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid="social-youtube">
                    <Youtube size={20} />
                  </a>
                )}
                {artist?.social?.instagram && (
                  <a href={artist.social.instagram} target="_blank" rel="noopener noreferrer" className="p-3 bg-[#262626] hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid="social-instagram">
                    <Instagram size={20} />
                  </a>
                )}
                {artist?.social?.tiktok && (
                  <a href={artist.social.tiktok} target="_blank" rel="noopener noreferrer" className="p-3 bg-[#262626] hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid="social-tiktok">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                )}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] border-2 border-[#D4AF37] overflow-hidden">
                <img
                  src={settings.profile_image_url || artist?.images?.profile || 'https://customer-assets.emergentagent.com/job_promptlab-42/artifacts/t5qvh4o1_1000002516.jpg'}
                  alt="IAMHETHATIS"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="video-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Watch</p>
            <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl">LATEST VIDEO</h2>
          </div>
          
          <div className="aspect-video bg-[#1A1A1A] border border-white/10 overflow-hidden">
            {/* YouTube embed placeholder - will be dynamic when videos are added */}
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]">
              <Link to="/videos" className="text-center">
                <Play size={64} className="mx-auto text-[#D4AF37] mb-4" />
                <p className="text-[#A3A3A3]">Explore All Videos</p>
              </Link>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/videos"
              className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline"
              data-testid="view-all-videos"
            >
              View All Videos
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="newsletter-section">
        <div className="absolute inset-0 opacity-20">
          <div
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url(${settings.newsletter_bg_url || 'https://images.unsplash.com/photo-1760978632119-5069b6c9fb68'})` }}
          />
        </div>
        
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Stay Connected</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl mb-4">JOIN THE MOVEMENT</h2>
          <p className="text-[#A3A3A3] mb-8">
            Get exclusive updates on new music, merch drops, and special announcements.
          </p>

          {subscribed ? (
            <div className="text-[#D4AF37] font-medium">
              Thank you for subscribing!
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 bg-transparent border-b-2 border-white/20 focus:border-[#D4AF37] focus:outline-none text-white placeholder-[#737373] transition-colors"
                data-testid="newsletter-email"
                required
              />
              <button
                type="submit"
                className="px-8 py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors flex items-center justify-center gap-2"
                data-testid="newsletter-submit"
              >
                <Send size={18} />
                Subscribe
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] border-2 border-[#D4AF37] flex items-center justify-center">
                <div className="w-5 h-6 bg-[#D4AF37]" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
              </div>
              <span className="font-['Bebas_Neue'] text-lg tracking-wider">YOUNG BLACK MAN INC.</span>
            </div>
            
            <p className="text-sm text-[#737373]">
              © 2025 Young Black Man Inc. All rights reserved.
            </p>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[#A3A3A3]">
              <a
                href="https://locallawfinder.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#D4AF37] transition-colors"
                data-testid="footer-locallawfinder"
              >
                LocalLawFinder
              </a>
              <Link to="/advertise" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-advertise">Advertise With Us</Link>
              <Link to="/contact" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-contact">Contact</Link>
              <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
